import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Plus, X, Check, ArrowLeft } from "lucide-react";
import { CandidateAvatar } from "../CandidateComponents";
import {
    INVALID_EMAIL_MESSAGE,
    INVALID_PHONE_MESSAGE,
    isValidEmail,
    isValidPhone,
    PhoneNumberInput,
} from "../ContactFields";
import { enrollCandidates, extractError } from "./enrollmentApi";

const SOURCING_URL = process.env.REACT_APP_SOURCING_API_URL;

const hasEmail = (c) => !!(c.email || c.professional_email || c.personal_email || c.has_business_email || c.has_personal_email);
const hasPhone = (c) => !!(c.phone || c.has_phone_number);
const contactOf = (c) => c.email || c.professional_email || c.personal_email || c.phone || null;

/**
 * Add-to-outreach flow for one or many candidates.
 *   Step "sequence" — pick an outreach (always first).
 *   Step "gate"     — only entered if, given the chosen outreach's channels, some
 *                      candidate is missing a required field; add manually or remove.
 *
 * Both steps end in the same choice: "Enroll" enrolls straight away on the outreach's
 * own templates, "Edit & Enroll" hands off to the preview so the copy can be tweaked
 * per candidate first.
 *
 * Props:
 *   candidates[]  – candidates to enroll (length 1 for the single-candidate flow)
 *   jobId         – job this journey is scoped to; stamped on the enrollment
 *   jobRole       – that job's title; what {{job_role}} resolves to in the copy
 *   onClose       – dismiss the modal
 *   onPreview     – called with (selectedSequence, list) for the "Edit & Enroll" path
 *   onEnrolled    – called with the enrollment summary for the direct "Enroll" path
 *   initialStep   – "gate" | "sequence" (default: "sequence")
 */
const AddToSequenceModal = ({ candidates, jobId, jobRole, onClose, onPreview, onEnrolled, initialStep }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const orgId = localStorage.getItem("current_org_id");

    const [list, setList] = useState(candidates || []);
    const [step, setStep] = useState(initialStep || "sequence");

    // gate step — per-row state
    const [busyId, setBusyId] = useState(null);
    const [manualFor, setManualFor] = useState(null);
    const [manualField, setManualField] = useState(null); // "email" | "phone"
    const [manualValue, setManualValue] = useState("");
    const [rowError, setRowError] = useState({});
    const [requiredEmail, setRequiredEmail] = useState(false);
    const [requiredPhone, setRequiredPhone] = useState(false);

    // sequence step
    const [sequences, setSequences] = useState([]);
    const [seqLoading, setSeqLoading] = useState(false);
    const [stepsLoading, setStepsLoading] = useState(false);
    const [seqSearch, setSeqSearch] = useState("");
    const [selectedSeqId, setSelectedSeqId] = useState("");
    const [enrollments, setEnrollments] = useState({}); // candidate_id -> enrollment | null
    const [enrolling, setEnrolling] = useState(false);

    const [error, setError] = useState(null);
    // Load failures replace the body; a failed action has to leave the list on screen,
    // so it gets its own line just above the buttons.
    const [actionError, setActionError] = useState(null);

    const alreadyEnrolled = list.filter(c => enrollments[c.candidate_id]);
    const unenrolled = list.filter(c => !enrollments[c.candidate_id]);
    const isReady = (c) => (!requiredEmail || hasEmail(c)) && (!requiredPhone || hasPhone(c));
    const incomplete = unenrolled.filter(c => !isReady(c));
    const ready = unenrolled.filter(isReady);
    const selectedSeq = sequences.find(s => s.id === selectedSeqId);
    const neededLabel = requiredEmail && requiredPhone ? "an email and a phone number" : requiredPhone ? "a phone number" : "an email";

    const updateCandidate = (id, patch) =>
        setList(prev => prev.map(c => (c.candidate_id === id ? { ...c, ...patch } : c)));

    const removeCandidate = (id) => setList(prev => prev.filter(c => c.candidate_id !== id));

    // ─── Gate step actions ────────────────────────────────────────────────────
    const startManual = (c, field) => {
        setManualFor(c.candidate_id);
        setManualField(field);
        setManualValue("");
    };

    const saveManual = async (c) => {
        const value = manualValue.trim();
        if (!value) return;
        const valid = manualField === "phone" ? isValidPhone(value) : isValidEmail(value);
        if (!valid) {
            setRowError(p => ({
                ...p,
                [c.candidate_id]: manualField === "phone" ? INVALID_PHONE_MESSAGE : INVALID_EMAIL_MESSAGE,
            }));
            return;
        }
        setBusyId(c.candidate_id);
        setRowError(p => ({ ...p, [c.candidate_id]: null }));
        try {
            const endpoint = manualField === "phone" ? "add-phone" : "add-email";
            const { data } = await axios.post(`${SOURCING_URL}/sourcing/${endpoint}`, {
                candidate_id: c.candidate_id,
                [manualField]: value,
            });
            updateCandidate(c.candidate_id, { [manualField]: data[manualField] });
            setManualFor(null);
            setManualField(null);
            setManualValue("");
        } catch {
            setRowError(p => ({ ...p, [c.candidate_id]: `Couldn't save that ${manualField}.` }));
        } finally {
            setBusyId(null);
        }
    };

    // ─── Sequence loading ─────────────────────────────────────────────────────
    useEffect(() => {
        if (step !== "sequence" || sequences.length > 0) return;
        setSeqLoading(true);
        Promise.all([
            axios.get(`${SOURCING_URL}/email/sequences`, { params: { org_id: orgId } }),
            ...list.map(c =>
                axios.get(`${SOURCING_URL}/email/enrollments`, {
                    params: { candidate_id: c.candidate_id, org_id: orgId },
                })
            ),
        ])
            .then(([seqRes, ...enrollResList]) => {
                setSequences(seqRes.data || []);
                setEnrollments(
                    Object.fromEntries(
                        list.map((c, i) => [
                            c.candidate_id,
                            enrollResList[i]?.data?.enrolled ? enrollResList[i].data.enrollment : null,
                        ])
                    )
                );
            })
            .catch(() => setError("Failed to load outreach."))
            .finally(() => setSeqLoading(false));
    }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

    const handlePreview = () => {
        if (!selectedSeq) return;
        onPreview(selectedSeq, list);
    };

    const runEnroll = async () => {
        const summary = await enrollCandidates({
            candidateIds: list.map(c => c.candidate_id),
            sequenceId: selectedSeq.id,
            jobId,
            jobRole,
        });
        onEnrolled?.(summary);
        onClose();
    };

    /**
     * Read the chosen outreach's steps to learn which contact fields it needs, then decide
     * whether this batch can go through untouched. Sends the batch to the gate step and
     * returns true when something needs fixing first.
     */
    const gateIfNeeded = async () => {
        const { data } = await axios.get(`${SOURCING_URL}/email/sequences/${selectedSeq.id}/steps`);
        const steps = data || [];
        const needsEmail = steps.some(s => !!s.template_id);
        const needsPhone = steps.some(s => !!s.whatsapp_template_id || s.is_call_step);
        setRequiredEmail(needsEmail);
        setRequiredPhone(needsPhone);
        const readyCheck = (c) => (!needsEmail || hasEmail(c)) && (!needsPhone || hasPhone(c));
        const stillEnrolled = list.some(c => enrollments[c.candidate_id]);
        const stillIncomplete = list.some(c => !enrollments[c.candidate_id] && !readyCheck(c));
        if (stillEnrolled || stillIncomplete) {
            setStep("gate");
            return true;
        }
        return false;
    };

    // "Edit & Enroll" — gate first, otherwise hand off to the preview.
    const handleEditAndEnroll = async () => {
        if (!selectedSeq) return;
        setStepsLoading(true);
        setActionError(null);
        try {
            if (!(await gateIfNeeded())) onPreview(selectedSeq, list);
        } catch {
            setActionError("Failed to load outreach steps.");
        } finally {
            setStepsLoading(false);
        }
    };

    // "Enroll" — gate first, otherwise enroll straight away on the outreach's templates.
    const handleEnrollNow = async () => {
        if (!selectedSeq) return;
        setEnrolling(true);
        setActionError(null);
        try {
            if (!(await gateIfNeeded())) await runEnroll();
        } catch (e) {
            setActionError(extractError(e, "Enrollment failed."));
        } finally {
            setEnrolling(false);
        }
    };

    // From the gate the requirements are already known, so enroll without re-checking.
    const handleGateEnroll = async () => {
        if (!selectedSeq) return;
        setEnrolling(true);
        setActionError(null);
        try {
            await runEnroll();
        } catch (e) {
            setActionError(extractError(e, "Enrollment failed."));
        } finally {
            setEnrolling(false);
        }
    };

    // ─── Render helpers ───────────────────────────────────────────────────────
    const ICON_TONES = {
        gray: "text-gray-400 hover:text-gray-600 hover:bg-gray-100",
        green: "text-gray-400 hover:text-green-600 hover:bg-green-50",
        red: "text-gray-400 hover:text-red-500 hover:bg-red-50",
    };
    const IconBtn = ({ onClick, title, tone = "gray", children }) => (
        <button onClick={onClick} title={title}
            className={`w-7 h-7 flex items-center justify-center rounded-full transition-all shrink-0 ${ICON_TONES[tone]}`}>
            {children}
        </button>
    );
    const Divider = () => <div className="w-px h-5 bg-gray-200 mx-0.5 shrink-0" />;

    const Row = ({ c, right }) => (
        <div className="flex items-center gap-3 px-3 py-2.5 rounded-xl border border-gray-100 dark:border-gray-700/50">
            <CandidateAvatar candidate={c} size={34} />
            <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-800 dark:text-white truncate">{c.full_name || "Unknown"}</p>
                <p className="text-xs text-gray-400 dark:text-gray-500 truncate">
                    {contactOf(c) || (c.current_title || "")}
                </p>
                {rowError[c.candidate_id] && <p className="text-xs text-red-500 dark:text-red-400 mt-0.5">{rowError[c.candidate_id]}</p>}
            </div>
            <div className="shrink-0">{right}</div>
        </div>
    );

    const shell = (title, subtitle, body, footer, onBack) => (
        <>
            <div className="fixed inset-0 bg-black/40 z-[60]" onClick={onClose} />
            <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 pointer-events-none">
                <div className="bg-white dark:bg-[#1B1524] rounded-2xl shadow-2xl w-full max-w-2xl max-h-[88vh] flex flex-col pointer-events-auto" onClick={e => e.stopPropagation()}>
                    <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-700/50 shrink-0">
                        <div className="flex items-center gap-3 min-w-0">
                            {onBack && (
                                <button onClick={onBack} className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-200 shrink-0">
                                    <ArrowLeft className="w-5 h-5" />
                                </button>
                            )}
                            <div className="min-w-0">
                                <h2 className="text-base font-semibold text-gray-900 dark:text-white truncate">{title}</h2>
                                {subtitle && <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{subtitle}</p>}
                            </div>
                        </div>
                        <button onClick={onClose} className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-200 shrink-0">
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                    <div className="flex-1 overflow-y-auto px-6 py-4">{body}</div>
                    {actionError && (
                        <p className="px-6 pb-1 text-xs text-red-500 dark:text-red-400 text-right shrink-0">{actionError}</p>
                    )}
                    <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-700/50 flex items-center justify-between gap-3 shrink-0">{footer}</div>
                </div>
            </div>
        </>
    );

    const cancelBtn = (
        <button onClick={onClose} className="px-5 py-2 rounded-full text-sm text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500">
            Cancel
        </button>
    );
    const primaryBtn = (label, onClick, disabled) => (
        <button onClick={onClick} disabled={disabled}
            className="px-5 py-2 rounded-full text-sm font-medium text-white disabled:opacity-40"
            style={{ background: "linear-gradient(to right, #ec4899, #3b82f6)" }}>
            {label}
        </button>
    );
    const secondaryBtn = (label, onClick, disabled) => (
        <button onClick={onClick} disabled={disabled}
            className="px-5 py-2 rounded-full text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:border-purple-400 hover:text-purple-600 disabled:opacity-40 disabled:hover:border-gray-300 disabled:hover:text-gray-700 transition-all">
            {label}
        </button>
    );

    /** Both steps end in the same pair: edit the copy first, or enroll as-is. */
    const enrollActions = (onEdit, onEnroll, disabled) => (
        <div className="flex items-center gap-2">
            {secondaryBtn(stepsLoading ? "Checking..." : "Edit & Enroll", onEdit, disabled || stepsLoading || enrolling)}
            {primaryBtn(enrolling ? "Enrolling..." : "Enroll", onEnroll, disabled || stepsLoading || enrolling)}
        </div>
    );

    // ─── Step: gate ───────────────────────────────────────────────────────────
    if (step === "gate") {
        return shell(
            list.length === 1 ? "Add contact info" : `Add ${list.length} candidates to an outreach`,
            `This outreach requires ${neededLabel} for each candidate`,
            <div className="flex flex-col gap-4">
                {alreadyEnrolled.length > 0 && (
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                                Already enrolled ({alreadyEnrolled.length})
                            </p>
                            <button
                                onClick={() => setList(prev => prev.filter(c => !enrollments[c.candidate_id]))}
                                className="text-xs font-medium text-red-500 dark:text-red-400 hover:text-red-600"
                            >
                                Remove all
                            </button>
                        </div>
                        <div className="flex flex-col gap-2">
                            {alreadyEnrolled.map(c => {
                                const seqName = sequences.find(s => s.id === enrollments[c.candidate_id]?.sequence_id)?.name || "an outreach";
                                return (
                                    <Row key={c.candidate_id} c={c} right={
                                        <span className="text-xs text-amber-600 dark:text-amber-300 whitespace-nowrap">In {seqName}</span>
                                    } />
                                );
                            })}
                        </div>
                    </div>
                )}

                {incomplete.length > 0 && (
                    <div className="flex items-start gap-2 px-4 py-3 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/40 rounded-xl text-sm text-amber-700 dark:text-amber-300">
                        <span>
                            <span className="font-semibold">{incomplete.length}</span> candidate{incomplete.length !== 1 ? "s don't" : " doesn't"} have {neededLabel}.
                            Add it manually{list.length > 1 ? ", or remove them to continue" : ""}.
                        </span>
                    </div>
                )}

                {incomplete.length > 0 && (
                    <div>
                        <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Needs contact info ({incomplete.length})</p>
                        <div className="flex flex-col gap-2">
                            {incomplete.map(c => (
                                <div key={c.candidate_id}>
                                    <Row c={c} right={
                                        manualFor === c.candidate_id ? null : (
                                            <div className="flex items-center gap-1.5">
                                                {requiredEmail && !hasEmail(c) && (
                                                    <button onClick={() => startManual(c, "email")}
                                                        className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-white/5">
                                                        <Plus className="w-3.5 h-3.5" /> Email
                                                    </button>
                                                )}
                                                {requiredPhone && !hasPhone(c) && (
                                                    <button onClick={() => startManual(c, "phone")}
                                                        className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-white/5">
                                                        <Plus className="w-3.5 h-3.5" /> Phone
                                                    </button>
                                                )}
                                                <Divider />
                                                <IconBtn onClick={() => removeCandidate(c.candidate_id)} title="Remove from this batch" tone="red">
                                                    <X className="w-4 h-4" />
                                                </IconBtn>
                                            </div>
                                        )
                                    } />
                                    {manualFor === c.candidate_id && (
                                        <div className="flex items-center gap-2 mt-1.5 pl-12">
                                            {manualField === "phone" ? (
                                                <PhoneNumberInput
                                                    autoComplete="off" autoFocus value={manualValue}
                                                    onChange={value => { setManualValue(value); setRowError(p => ({ ...p, [c.candidate_id]: null })); }}
                                                    onKeyDown={e => { if (e.key === "Enter") saveManual(c); }}
                                                    placeholder="+91 98765 43210"
                                                    className="flex-1 text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-1.5 focus-within:border-purple-400"
                                                />
                                            ) : (
                                                <input
                                                    type="email" autoComplete="off" autoFocus value={manualValue}
                                                    onChange={e => { setManualValue(e.target.value); setRowError(p => ({ ...p, [c.candidate_id]: null })); }}
                                                    onKeyDown={e => { if (e.key === "Enter") saveManual(c); }}
                                                    placeholder="name@company.com"
                                                    className="flex-1 text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-1.5 outline-none focus:border-purple-400"
                                                />
                                            )}
                                            <button onClick={() => saveManual(c)} disabled={busyId === c.candidate_id || !manualValue.trim()}
                                                className="px-3 py-1.5 rounded-lg text-xs font-medium text-white disabled:opacity-40"
                                                style={{ background: "linear-gradient(to right, #ec4899, #3b82f6)" }}>
                                                Save
                                            </button>
                                            <button onClick={() => { setManualFor(null); setManualField(null); }} className="px-2 py-1.5 text-xs text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-200">Cancel</button>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {ready.length > 0 && (
                    <div>
                        <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                            <Check className="w-3.5 h-3.5 text-green-500" /> Ready ({ready.length})
                        </p>
                        <div className="flex flex-col gap-2">
                            {ready.map(c => (
                                <Row key={c.candidate_id} c={c} right={
                                    list.length > 1 ? (
                                        <IconBtn onClick={() => removeCandidate(c.candidate_id)} title="Remove from this batch" tone="red">
                                            <X className="w-4 h-4" />
                                        </IconBtn>
                                    ) : null
                                } />
                            ))}
                        </div>
                    </div>
                )}

                {list.length === 0 && <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-8">No candidates left. Add some back or cancel.</p>}
            </div>,
            <>
                {cancelBtn}
                {enrollActions(
                    handlePreview,
                    handleGateEnroll,
                    incomplete.length > 0 || alreadyEnrolled.length > 0 || list.length === 0
                )}
            </>,
            () => setStep("sequence")
        );
    }

    // ─── Step: sequence select ────────────────────────────────────────────────
    const filteredSequences = sequences.filter(s => s.name.toLowerCase().includes(seqSearch.toLowerCase()));
    const singleEnrollment = list.length === 1 ? enrollments[list[0]?.candidate_id] : null;

    return shell(
        list.length === 1 ? "Enroll in Outreach" : "Select an outreach",
        list.length === 1 ? list[0].full_name : `${list.length} recipients`,
        seqLoading ? (
            <div className="flex justify-center py-10"><span className="w-5 h-5 border-2 border-purple-400 border-t-transparent rounded-full animate-spin" /></div>
        ) : error ? (
            <p className="text-sm text-red-500 dark:text-red-400 text-center py-10">{error}</p>
        ) : singleEnrollment ? (
            <div className="flex flex-col items-center text-center gap-2 py-12">
                <CandidateAvatar candidate={list[0]} size={44} />
                <p className="text-sm font-medium text-gray-800 dark:text-white mt-1">{list[0].full_name} is already in an outreach</p>
                <p className="text-xs text-gray-400 dark:text-gray-500">
                    Currently enrolled in{" "}
                    <span className="font-medium text-amber-600 dark:text-amber-300">
                        {sequences.find(s => s.id === singleEnrollment.sequence_id)?.name || "an outreach"}
                    </span>
                </p>
            </div>
        ) : sequences.length === 0 ? (
            <div className="text-center py-10">
                <p className="text-sm font-semibold text-gray-800 dark:text-white">No outreach yet</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Create an outreach to start reaching out to candidates.</p>
            </div>
        ) : (
            <div className="flex flex-col gap-2">
                <input value={seqSearch} onChange={e => setSeqSearch(e.target.value)} placeholder="Search outreach..." name="sequence-search" autoComplete="off"
                    className="w-full text-sm border border-gray-200 dark:border-gray-600 rounded-xl px-3 py-2 outline-none focus:border-purple-400 mb-1" />
                {filteredSequences.length === 0 ? (
                    <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-6">No outreach matches your search.</p>
                ) : (
                    filteredSequences.map(seq => (
                        <button key={seq.id} onClick={() => setSelectedSeqId(seq.id)}
                            className={`w-full text-left px-4 py-3 rounded-xl border transition-all ${selectedSeqId === seq.id ? "border-purple-400 bg-purple-50" : "border-gray-200 hover:border-gray-300"}`}>
                            <p className={`text-sm font-medium flex items-center gap-2 ${selectedSeqId === seq.id ? "text-purple-700" : "text-gray-800"}`}>
                                {seq.name}
                                {seq.type === "screening" && <span className="px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-600 dark:text-purple-300 text-[10px] font-medium">Screening</span>}
                            </p>
                            {seq.description && <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 truncate">{seq.description}</p>}
                        </button>
                    ))
                )}
                <button
                    onClick={() => { onClose(); navigate("/org-admin/sourcing/sequences/new", { state: { returnTo: location.pathname } }); }}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 mt-1 rounded-xl border border-dashed border-purple-300 text-purple-600 dark:text-purple-300 text-sm font-medium hover:bg-purple-50 transition-all"
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Create an outreach
                </button>
            </div>
        ),
        singleEnrollment ? (
            <div className="flex justify-end w-full">
                <button onClick={onClose} className="px-5 py-2 rounded-full text-sm text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500">
                    Close
                </button>
            </div>
        ) : (
            <>
                {cancelBtn}
                {enrollActions(handleEditAndEnroll, handleEnrollNow, !selectedSeqId || list.length === 0)}
            </>
        ),
        undefined
    );
};

export default AddToSequenceModal;
